import { Component, Input } from '@angular/core'
import { closeJobSelectionDialog } from '../../../shared/utils/job-selection-dialog-utils'
import { Store } from '@ngrx/store'
import { JobApiService } from '../../../shared/services/job-api.service'
import { ToasterService } from '../../../shared/services/toaster.service'
import { map, Observable, takeUntil, Subject, combineLatest } from 'rxjs'
import {
    selectSavedFreeSelectionNames,
    selectAllSavedHierarchicalSelectionNodeItems,
    selectAllSavedUnsupervisedSelectionNodeItems,
} from '../../../state/selection/selection.selectors'
import { HttpErrorResponse } from '@angular/common/http'
import { PipelineType } from '../../../shared/model/jobs'
import { environment } from '../../../../environments/environment'

@Component({
    selector: 'app-neighborhood-enrichment-selection',
    templateUrl: './neighborhood-enrichment-selection.component.html',
    styleUrls: ['./neighborhood-enrichment-selection.component.scss'],
})
export class NeighborhoodEnrichmentSelectionComponent {
    @Input()
    siblingDatasets: string[] | never[]
    selections$: Observable<{ name: string; type: number }[]>
    private selectedSelectionNames: { name: string; type: number }[] | null = null

    experimentName = ''
    keepBackground = false
    radius = 1
    submitted = false
    runBatchJob: boolean = false

    private readonly wikiUrl = environment.wikiUrl
    private readonly onDestroy$ = new Subject<void>()

    constructor(
        private store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService
    ) {
        this.selections$ = combineLatest([
            this.store.select(selectSavedFreeSelectionNames),
            this.store.select(selectAllSavedHierarchicalSelectionNodeItems),
            this.store.select(selectAllSavedUnsupervisedSelectionNodeItems),
        ]).pipe(
            takeUntil(this.onDestroy$),
            map(
                ([
                    freeSelectionsName,
                    hierarchicalSelectionsGroup,
                    unsupervisedSelectionsGroup,
                ]) => {
                    const freeSelectionNames = freeSelectionsName.map(name => ({
                        name: name,
                        type: 1,
                    }))
                    const hierarchicalNames = hierarchicalSelectionsGroup
                        .map(g => g.tree.map(t => `[${g.groupName}] ${t.name}`))
                        .flat()
                        .map(name => ({ name: name, type: 2 }))
                    const unsupervisedNames = unsupervisedSelectionsGroup
                        .map(g => g.tree.map(t => `[${g.groupName}] ${t.name}`))
                        .flat()
                        .map(name => ({ name: name, type: 3 }))

                    return [...freeSelectionNames, ...hierarchicalNames, ...unsupervisedNames]
                }
            )
        )
    }

    get canStart(): boolean {
        return (
            this.radius > 0 &&
            this.experimentName.length > 0 &&
            !!this.selectedSelectionNames &&
            this.selectedSelectionNames.length > 0 &&
            !this.submitted
        )
    }

    cancel(): void {
        closeJobSelectionDialog()
    }

    startJob(): void {
        if (!this.canStart) return
        this.submitted = true

        this.jobApi
            .startNeighborhoodEnrichmentJob({
                type: PipelineType.NeighborhoodEnrichment,
                selections: this.selectedSelectionNames!,
                experimentName: this.experimentName,
                keepBackground: this.keepBackground,
                radius: this.radius,
                batch: this.runBatchJob,
            })
            .subscribe({
                next: () => closeJobSelectionDialog(),
                error: (err: HttpErrorResponse) => {
                    this.toaster.httpError(err, 'Error starting job')
                },
            })
    }

    selectSelections(selections: { name: string; type: number }[]): void {
        this.selectedSelectionNames = selections
    }

    goToWiki(): void {
        window.open(
            `${this.wikiUrl}home/Navigation#clustering-analysis-neighborhood-enrichment`,
            '_blank'
        )
    }
}
